export type Locale = "en" | "fr" | "de";
export type PageKey = "home" | "services" | "case-studies" | "strategy" | "tiers" | "contact";

export const locales: Locale[] = ["en", "fr", "de"];

export const localeLabels: Record<Locale, string> = {
  en: "EN",
  fr: "FR",
  de: "DE"
};

export const pageSlugs: Record<PageKey, string> = {
  home: "",
  services: "services",
  "case-studies": "case-studies",
  strategy: "strategy",
  tiers: "tiers",
  contact: "contact"
};

export function localeFromPath(pathname: string): Locale {
  if (pathname === "/fr" || pathname.startsWith("/fr/")) return "fr";
  if (pathname === "/de" || pathname.startsWith("/de/")) return "de";
  return "en";
}

export function stripLocaleFromPath(pathname: string) {
  if (pathname === "/fr" || pathname === "/de") return "/";
  if (pathname.startsWith("/fr/") || pathname.startsWith("/de/")) return pathname.slice(3) || "/";
  return pathname;
}

export function localePath(locale: Locale, page: PageKey) {
  const slug = pageSlugs[page];
  const localePrefix = locale === "en" ? "" : `/${locale}`;
  return slug ? `${localePrefix}/${slug}/` : locale === "en" ? "/" : `${localePrefix}/`;
}

export interface SiteContent {
  meta: Record<PageKey, { title: string; description: string }>;
  nav: Array<{ page: PageKey; label: string }>;
  ui: {
    navLabel: string;
    mobileNavLabel: string;
    languageLabel: string;
    brandAria: string;
    brandSubline: string;
    cta: string;
    footer: string;
    openMenu: string;
    closeMenu: string;
    outcomesLabel: string;
    impactLabel: string;
    trustSignalsLabel: string;
  };
  outcomes: string[];
  trustSignals: Array<{ value: string; label: string }>;
  services: Array<{ title: string; text: string; points: string[] }>;
  caseStudies: Array<{ sector: string; title: string; result: string; text: string; metrics: string[] }>;
  strategySteps: Array<{ step: string; title: string; text: string; output: string }>;
  serviceTiers: Array<{
    name: string;
    price: string;
    monthly: string;
    description: string;
    featured?: boolean;
    features: string[];
  }>;
  faqItems: Array<[string, string]>;
  home: {
    eyebrow: string;
    title: string;
    lede: string;
    primaryCta: string;
    secondaryCta: string;
    leadKicker: string;
    leadTitle: string;
    leadText: string;
    leadButton: string;
  };
  servicesPage: {
    kicker: string;
    title: string;
    text: string;
    deliveryKicker: string;
    deliveryTitle: string;
    deliveryText: string;
    useCases: string[];
  };
  caseStudiesPage: {
    kicker: string;
    title: string;
    text: string;
    proofKicker: string;
    proofTitle: string;
    proofText: string;
    proofCards: Array<[string, string]>;
  };
  strategyPage: {
    kicker: string;
    title: string;
    text: string;
    winsKicker: string;
    winsTitle: string;
    winsText: string;
    wins: string[];
  };
  tiersPage: {
    kicker: string;
    title: string;
    text: string;
    fitKicker: string;
    fitTitle: string;
    fitText: string;
    fitButton: string;
    mostPopular: string;
    serviceTier: string;
    discuss: string;
  };
  contactPage: {
    kicker: string;
    title: string;
    text: string;
    intakeKicker: string;
    intakeTitle: string;
    intakeText: string;
    compareTiers: string;
    button: string;
  };
  heroVisual: {
    aria: string;
    cockpit: string;
    status: string;
    tabsAria: string;
    businessWorkflow: string;
    aiAgent: string;
    humanApproval: string;
    signal: string;
    result: string;
  };
  sections: {
    consultingServices: string;
    ambitionTitle: string;
    ambitionText: string;
    caseStudiesKicker: string;
    caseStudiesTitle: string;
    caseStudiesText: string;
    exploreCaseStudies: string;
    methodKicker: string;
    methodTitle: string;
    questionsKicker: string;
    questionsTitle: string;
  };
  form: {
    name: string;
    email: string;
    company: string;
    phone: string;
    industry: string;
    teamSize: string;
    serviceTier: string;
    recommendOne: string;
    timeline: string;
    preferredContact: string;
    improve: string;
    improvePlaceholder: string;
    priorityNeed: string;
    select: string;
    any: string;
    sending: string;
    success: string;
    error: string;
    note: string;
    industries: string[];
    timelines: string[];
    preferredContacts: string[];
  };
}

export const content: Record<Locale, SiteContent> = {
  en: {
    meta: {
      home: {
        title: "Schwartz AI Partners - Enterprise-Grade AI Consulting for SMBs",
        description:
          "Schwartz AI Partners helps small and medium businesses define AI strategy, build AI agents and automation, govern adoption, and measure ROI."
      },
      services: {
        title: "Schwartz AI Partners Services",
        description:
          "AI opportunity audits, AI agents, automation, data integration, governance, and adoption for small and medium businesses."
      },
      "case-studies": {
        title: "Schwartz AI Partners Case Studies",
        description:
          "Representative AI consulting case studies for law firms, lead generation firms, ecommerce, online education, sales operations, healthcare intake, manufacturing knowledge, and SMB operations."
      },
      strategy: {
        title: "Schwartz AI Partners Strategy",
        description:
          "A practical AI strategy process for SMB outreach, closing, law firm intake, lead generation, online operations, workflow automation, governance, and rollout."
      },
      tiers: {
        title: "Schwartz AI Partners Service Tiers",
        description:
          "AI consulting service tiers for strategy sprints, pilot builds, and ongoing AI operating partner support."
      },
      contact: {
        title: "Contact Schwartz AI Partners",
        description: "Request an AI roadmap session for your small or medium business."
      }
    },
    nav: [
      { page: "services", label: "Services" },
      { page: "case-studies", label: "Case studies" },
      { page: "strategy", label: "Strategy" },
      { page: "tiers", label: "Tiers" },
      { page: "contact", label: "Contact" }
    ],
    ui: {
      navLabel: "Main navigation",
      mobileNavLabel: "Mobile navigation",
      languageLabel: "Language",
      brandAria: "Schwartz AI Partners home",
      brandSubline: "Enterprise-grade AI for SMBs",
      cta: "Book strategy call",
      footer: "Strategy, implementation, and adoption for small and medium businesses.",
      openMenu: "Open menu",
      closeMenu: "Close menu",
      outcomesLabel: "Expected consulting outcomes",
      impactLabel: "Consulting impact",
      trustSignalsLabel: "AI consulting trust signals"
    },
    outcomes: [
      "Clear AI roadmap in 10 business days",
      "First automation or agent shipped in 30 to 90 days",
      "Governance, adoption, and ROI tracking built in from day one"
    ],
    trustSignals: [
      { value: "SMB", label: "Built for small and medium businesses" },
      { value: "30-90", label: "Days to first production workflow" },
      { value: "Ops + AI", label: "Strategy, build, integration, and adoption" },
      { value: "Human-led", label: "Controls, review paths, and governance" }
    ],
    services: [
      {
        title: "AI Opportunity Audit",
        text: "Map workflows, data, software, and team friction to define the clearest path to useful AI.",
        points: ["Use-case portfolio", "ROI and effort scoring", "Risk and data readiness review"]
      },
      {
        title: "AI Agents and Automation",
        text:
          "Design, implement, and connect AI agents that handle repeatable knowledge work across operations, sales, support, and admin.",
        points: ["CRM and email agents", "Document workflows", "Human approval paths"]
      },
      {
        title: "Data and Tool Integration",
        text: "Connect AI to the systems your company already uses without forcing a platform migration.",
        points: ["CRM, spreadsheets, inboxes", "Knowledge bases", "APIs and internal tools"]
      },
      {
        title: "Governance and Adoption",
        text: "Set policies, permissions, validation rules, and training so teams actually use the systems safely.",
        points: ["AI usage policy", "Team enablement", "Monitoring and improvement"]
      }
    ],
    caseStudies: [
      {
        sector: "B2B Services",
        title: "Sales operations copilot",
        result: "12 hours saved per week",
        text:
          "A growing service firm needed cleaner lead follow-up and proposal prep. We connected CRM notes, email history, and templates into a guided workflow that drafts follow-ups and prepares account briefs for review.",
        metrics: ["Lead response time down 68%", "Pipeline notes standardized", "No CRM migration"]
      },
      {
        sector: "Local Healthcare",
        title: "Patient intake and admin automation",
        result: "2.4x faster intake",
        text:
          "A clinic team was spending too much time turning forms, calls, and emails into structured tasks. We built a controlled intake workflow that summarizes requests, tags urgency, and routes work to the right staff member.",
        metrics: ["40+ requests handled daily", "Human review preserved", "Clear audit trail"]
      },
      {
        sector: "Light Manufacturing",
        title: "Operations knowledge assistant",
        result: "Fewer repeated interruptions",
        text:
          "A manufacturing SMB had procedures scattered across files and tribal knowledge. We created a searchable assistant over SOPs, supplier docs, and internal notes, with escalation when confidence is low.",
        metrics: ["SOP retrieval in seconds", "Supplier lookup automated", "Low-confidence handoff"]
      },
      {
        sector: "Law Firms",
        title: "Client intake and matter briefing",
        result: "Same-day intake summaries",
        text:
          "A small law firm needed faster intake without losing professional review. We designed a workflow that captures web and phone inquiries, summarizes the matter, drafts follow-up questions, and prepares an attorney-ready briefing.",
        metrics: ["New inquiries structured", "Attorney review preserved", "Conflict-check prep included"]
      },
      {
        sector: "Lead Generation",
        title: "Lead qualification and handoff agent",
        result: "Higher booked-call quality",
        text:
          "A lead generation firm was qualifying prospects manually across forms, emails, CRM notes, and call outcomes. We built an agent-assisted workflow that enriches leads, scores fit, drafts follow-ups, and flags weak handoffs before sales calls.",
        metrics: ["Fit scoring standardized", "Follow-up drafts automated", "CRM handoffs cleaner"]
      },
      {
        sector: "Ecommerce",
        title: "Customer support and returns triage",
        result: "24/7 buyer support coverage",
        text:
          "An online store needed better customer response without adding a full support team. We connected order data, product information, shipping policies, and helpdesk history into a controlled support assistant with escalation rules.",
        metrics: ["Order questions handled faster", "Returns triaged automatically", "Escalation rules enforced"]
      },
      {
        sector: "Online Education",
        title: "Enrollment and student support copilot",
        result: "Faster lead-to-enrollment response",
        text:
          "A course and coaching business needed to follow up with leads, answer program questions, and support enrolled students across multiple channels. We built a workflow for qualification, reminders, onboarding, and knowledge-base answers.",
        metrics: ["Inquiry follow-up accelerated", "Student FAQs automated", "Program fit notes captured"]
      }
    ],
    strategySteps: [
      {
        step: "01",
        title: "Diagnose",
        text:
          "We interview stakeholders, inspect workflows, and identify where AI can remove bottlenecks without creating operational risk.",
        output: "AI opportunity map and executive summary"
      },
      {
        step: "02",
        title: "Prioritize",
        text: "We score use cases by value, complexity, data readiness, urgency, and adoption friction.",
        output: "Ranked roadmap with budget bands"
      },
      {
        step: "03",
        title: "Build",
        text: "We ship the first agent, automation, or copilot around your real tools, roles, and approval rules.",
        output: "Production-ready pilot with governance"
      },
      {
        step: "04",
        title: "Scale",
        text:
          "We measure usage, tune workflows, train teams, and roll the operating model into the next departments.",
        output: "Measured rollout plan and operating cadence"
      }
    ],
    serviceTiers: [
      {
        name: "AI Strategy Sprint",
        price: "From $2,500",
        monthly: "10 business days",
        description: "For owners and leadership teams who need a clear path before investing in tools or headcount.",
        features: [
          "Workflow and software review",
          "AI opportunity portfolio",
          "ROI and risk scoring",
          "Executive roadmap presentation",
          "First pilot recommendation"
        ]
      },
      {
        name: "Pilot Build",
        price: "From $7,500",
        monthly: "30-60 days",
        description: "For teams ready to turn one high-value use case into a working AI workflow with real adoption.",
        featured: true,
        features: [
          "One production-grade AI workflow",
          "Tool and data integration",
          "Human approval and escalation rules",
          "Team onboarding session",
          "Success metrics dashboard"
        ]
      },
      {
        name: "AI Operating Partner",
        price: "From $3,500/mo",
        monthly: "Quarterly roadmap",
        description:
          "For SMBs that want ongoing AI delivery, governance, and optimization without hiring a full internal AI team.",
        features: [
          "Monthly build and optimization cycles",
          "Governance and policy support",
          "Vendor and model selection",
          "Training and adoption reviews",
          "Leadership reporting"
        ]
      }
    ],
    faqItems: [
      [
        "Are you only for enterprise companies?",
        "No. The brand standard is enterprise-grade, but the delivery model is built for small and medium businesses that need practical AI outcomes without enterprise consulting overhead."
      ],
      [
        "Do you just advise, or do you build?",
        "Both. We define the strategy, then build the first workflow or agent so the roadmap turns into measurable operating change."
      ],
      [
        "Can you use our existing software?",
        "Yes. We usually start with the systems already in place: CRM, email, spreadsheets, documents, calendars, support tools, and internal databases."
      ],
      [
        "How do you reduce AI risk?",
        "We design workflows with data boundaries, human approval, audit logs, escalation rules, and team training instead of treating AI as a black box."
      ]
    ],
    home: {
      eyebrow: "The clear path to enterprise-grade AI",
      title: "AI strategy and agents your business can actually use.",
      lede:
        "Schwartz AI Partners helps small and medium businesses move from AI uncertainty to practical systems that improve operations, sales, support, and decision-making. We define the roadmap, build the first workflows, and help your team adopt them with clear controls.",
      primaryCta: "Plan your AI roadmap",
      secondaryCta: "View use cases",
      leadKicker: "Outreach and closing",
      leadTitle: "Start with the use cases most likely to win budget and team adoption.",
      leadText:
        "Send your company size, tools, and main bottleneck. We will identify the clearest first AI project and the right path to implementation.",
      leadButton: "Request AI strategy review"
    },
    servicesPage: {
      kicker: "Services",
      title: "AI consulting that turns strategy into a clear operating path.",
      text:
        "We combine executive strategy, implementation, and adoption support so your AI program is not a slide deck, a chatbot experiment, or a vendor shopping exercise.",
      deliveryKicker: "Delivery model",
      deliveryTitle: "Senior consulting discipline, practical SMB execution.",
      deliveryText:
        "Our work is designed for companies that need structured thinking, responsible implementation, and a clear sequence of wins without building a large internal AI function.",
      useCases: [
        "Operations automation",
        "Sales and CRM copilots",
        "Customer support agents",
        "Document and reporting workflows",
        "Law firm intake and matter summaries",
        "Lead qualification and enrichment",
        "Ecommerce support and returns triage",
        "Online course onboarding workflows",
        "Knowledge assistants",
        "AI governance and training"
      ]
    },
    caseStudiesPage: {
      kicker: "Case studies",
      title: "Representative AI transformations for growing companies.",
      text:
        "These examples show the kind of work we prioritize: practical, measurable workflows that reduce manual effort and improve customer or employee experience.",
      proofKicker: "Trust signals",
      proofTitle: "What every engagement includes.",
      proofText:
        "We keep projects grounded in operating reality: real software, real teams, clear controls, and measurable output.",
      proofCards: [
        ["Executive sponsor alignment", "A clear owner, decision process, and commercial objective."],
        ["Data boundary review", "What AI can access, what it cannot, and where humans stay in control."],
        ["Adoption plan", "Training, workflow documentation, and team feedback loops."],
        ["ROI dashboard", "Usage, time saved, conversion, cycle time, or quality metrics."]
      ]
    },
    strategyPage: {
      kicker: "Strategy",
      title: "A clear AI path for outreach, closing, operations, and delivery.",
      text:
        "For small and medium businesses, AI strategy has to connect directly to revenue, margin, and daily execution. We build the roadmap around business outcomes, not model hype.",
      winsKicker: "For SMB leadership",
      winsTitle: "Where we usually find the first wins.",
      winsText:
        "The best first AI project is rarely the flashiest one. It is the workflow with enough volume, accessible data, clear ownership, and a measurable business result.",
      wins: [
        "Lead research, outreach drafting, and follow-up workflows",
        "Lead-gen agency prospect enrichment and booked-call QA",
        "Proposal, quote, and client briefing copilots",
        "Law firm intake, conflict-check prep, and matter summaries",
        "Support triage and knowledge-base agents",
        "Ecommerce product Q&A, order support, and returns triage",
        "Online course enrollment, onboarding, and student support",
        "Invoice, document, and admin extraction workflows",
        "Operations reporting and exception alerts",
        "Internal knowledge assistants for SOPs and onboarding"
      ]
    },
    tiersPage: {
      kicker: "Service tiers",
      title: "Choose the level of AI support your business can actually absorb.",
      text:
        "Start with strategy, move into a pilot, or retain us as an operating partner. Each tier is designed to create visible progress without overwhelming the team.",
      fitKicker: "Fit check",
      fitTitle: "Not sure which tier fits?",
      fitText:
        "Share your business context and we will recommend the clearest path from idea to useful AI workflow.",
      fitButton: "Ask about tiers",
      mostPopular: "Most popular",
      serviceTier: "Service tier",
      discuss: "Discuss"
    },
    contactPage: {
      kicker: "Contact",
      title: "Brief us on the AI opportunity you want to unlock.",
      text:
        "Tell us about your company, current tools, and the workflow you want to improve. We will reply with the likely first use case, project tier, and next step on the path.",
      intakeKicker: "Strategy intake",
      intakeTitle: "Request an AI roadmap session.",
      intakeText:
        "This form sends your request through our CRM workflow. Include your industry, team size, software stack, and the business process you want AI to improve first.",
      compareTiers: "Compare service tiers",
      button: "Send strategy brief"
    },
    heroVisual: {
      aria: "AI transformation operating model",
      cockpit: "Schwartz AI cockpit",
      status: "Roadmap live",
      tabsAria: "Choose AI consulting example",
      businessWorkflow: "Business workflow",
      aiAgent: "AI agent",
      humanApproval: "Human approval",
      signal: "Signal",
      result: "Strategy, build, controls, and adoption stay on one clear path."
    },
    sections: {
      consultingServices: "Consulting services",
      ambitionTitle: "From AI ambition to operating capability.",
      ambitionText:
        "We act like a practical transformation partner: clarify the opportunity, build the system, connect it to your tools, and help the team adopt it.",
      caseStudiesKicker: "Case studies",
      caseStudiesTitle: "Practical AI programs for businesses that need ROI, not theater.",
      caseStudiesText:
        "We focus on workflows where AI can shorten cycle time, reduce repetitive work, improve response quality, or unlock better follow-up.",
      exploreCaseStudies: "Explore case studies",
      methodKicker: "Transformation method",
      methodTitle: "A disciplined route from strategy to shipped AI.",
      questionsKicker: "Questions",
      questionsTitle: "What leadership teams ask first."
    },
    form: {
      name: "Name",
      email: "Work email",
      company: "Company",
      phone: "Phone",
      industry: "Industry",
      teamSize: "Team size",
      serviceTier: "Service tier",
      recommendOne: "Recommend one",
      timeline: "Timeline",
      preferredContact: "Preferred contact",
      improve: "What should AI improve first?",
      improvePlaceholder:
        "Example: lead follow-up, proposal creation, support triage, reporting, document workflows, internal knowledge search...",
      priorityNeed: "AI consulting strategy and implementation",
      select: "Select",
      any: "Any",
      sending: "Sending",
      success: "Thanks. Your strategy brief was sent to our CRM workflow.",
      error: "The CRM request failed. Email contact@schwartzaipartners.com and we will follow up.",
      note: "We use your details only to reply about your AI consulting opportunity.",
      industries: [
        "Professional services",
        "Law firm or legal services",
        "Lead generation agency",
        "Marketing or growth agency",
        "Online education or coaching",
        "Healthcare or clinics",
        "Manufacturing",
        "Retail or ecommerce",
        "SaaS or online platform",
        "Real estate or construction",
        "Hospitality",
        "Other SMB"
      ],
      timelines: ["This month", "This quarter", "Next quarter", "Exploring"],
      preferredContacts: ["Email", "Phone", "Video call"]
    }
  },
  fr: {
    meta: {
      home: {
        title: "Schwartz AI Partners - Conseil en IA de niveau entreprise pour PME",
        description:
          "Schwartz AI Partners aide les PME à définir leur stratégie IA, créer des agents et des automatisations, encadrer l'adoption et mesurer le ROI."
      },
      services: {
        title: "Services Schwartz AI Partners",
        description:
          "Audits d'opportunités IA, agents IA, automatisation, intégration des données, gouvernance et adoption pour PME."
      },
      "case-studies": {
        title: "Cas clients Schwartz AI Partners",
        description:
          "Exemples de conseil en IA pour cabinets d'avocats, agences de génération de leads, e-commerce, formation en ligne, ventes, santé de proximité, industrie et opérations PME."
      },
      strategy: {
        title: "Stratégie Schwartz AI Partners",
        description:
          "Une démarche IA concrète pour la prospection, la conversion commerciale, la qualification juridique, la génération de leads, les opérations en ligne, l'automatisation, la gouvernance et le déploiement."
      },
      tiers: {
        title: "Offres Schwartz AI Partners",
        description:
          "Offres de conseil en IA pour sprint stratégique, pilote opérationnel et accompagnement continu."
      },
      contact: {
        title: "Contacter Schwartz AI Partners",
        description: "Demandez une session de feuille de route IA pour votre PME."
      }
    },
    nav: [
      { page: "services", label: "Services" },
      { page: "case-studies", label: "Cas clients" },
      { page: "strategy", label: "Stratégie" },
      { page: "tiers", label: "Offres" },
      { page: "contact", label: "Contact" }
    ],
    ui: {
      navLabel: "Navigation principale",
      mobileNavLabel: "Navigation mobile",
      languageLabel: "Langue",
      brandAria: "Accueil Schwartz AI Partners",
      brandSubline: "IA de niveau entreprise pour PME",
      cta: "Réserver un échange",
      footer: "Stratégie, mise en œuvre et adoption pour petites et moyennes entreprises.",
      openMenu: "Ouvrir le menu",
      closeMenu: "Fermer le menu",
      outcomesLabel: "Résultats attendus du conseil",
      impactLabel: "Impact du conseil",
      trustSignalsLabel: "Signaux de confiance du conseil IA"
    },
    outcomes: [
      "Feuille de route IA claire en 10 jours ouvrables",
      "Première automatisation ou premier agent livré en 30 à 90 jours",
      "Gouvernance, adoption et suivi du ROI intégrés dès le premier jour"
    ],
    trustSignals: [
      { value: "PME", label: "Conçu pour les petites et moyennes entreprises" },
      { value: "30-90", label: "Jours jusqu'au premier processus en production" },
      { value: "Ops + IA", label: "Stratégie, conception, intégration et adoption" },
      { value: "Humain", label: "Contrôles, validations et gouvernance" }
    ],
    services: [
      {
        title: "Audit d'opportunités IA",
        text:
          "Cartographier les processus, les données, les outils et les frictions d'équipe pour définir le chemin le plus clair vers une IA réellement utile.",
        points: ["Portefeuille de cas d'usage", "Évaluation ROI et effort", "Revue des risques et de la préparation des données"]
      },
      {
        title: "Agents IA et automatisation",
        text:
          "Concevoir, mettre en place et connecter des agents IA qui prennent en charge le travail répétitif dans les opérations, les ventes, le support client et l'administratif.",
        points: ["Agents CRM et e-mail", "Processus documentaires", "Parcours de validation humaine"]
      },
      {
        title: "Intégration données et outils",
        text:
          "Connecter l'IA aux systèmes que votre entreprise utilise déjà, sans imposer une migration de plateforme.",
        points: ["CRM, tableurs, boîtes e-mail", "Bases de connaissances", "API et outils internes"]
      },
      {
        title: "Gouvernance et adoption",
        text:
          "Mettre en place les politiques, permissions, règles de validation et formations qui permettent aux équipes d'utiliser les systèmes de façon fiable.",
        points: ["Politique d'usage de l'IA", "Formation des équipes", "Suivi et amélioration continue"]
      }
    ],
    caseStudies: [
      {
        sector: "Services B2B",
        title: "Copilote d'opérations commerciales",
        result: "12 heures économisées par semaine",
        text:
          "Une société de services en croissance avait besoin d'un suivi commercial et d'une préparation de propositions plus fiables. Nous avons relié les notes CRM, l'historique e-mail et les modèles dans un processus guidé qui rédige les relances et prépare les synthèses de compte pour validation.",
        metrics: ["Temps de réponse aux leads réduit de 68 %", "Notes de pipeline standardisées", "Aucune migration CRM"]
      },
      {
        sector: "Santé de proximité",
        title: "Automatisation de l'admission patient",
        result: "Admission 2,4x plus rapide",
        text:
          "Une équipe de clinique passait trop de temps à transformer formulaires, appels et e-mails en tâches structurées. Nous avons construit un processus contrôlé qui résume les demandes, qualifie l'urgence et oriente le travail vers la bonne personne.",
        metrics: ["40+ demandes traitées par jour", "Validation humaine préservée", "Piste d'audit claire"]
      },
      {
        sector: "Industrie légère",
        title: "Assistant de connaissances opérationnelles",
        result: "Moins d'interruptions répétées",
        text:
          "Une PME industrielle avait des procédures dispersées entre fichiers, documents fournisseurs et savoir informel. Nous avons créé un assistant de recherche sur les procédures, les documents fournisseurs et les notes internes, avec escalade lorsque la confiance est faible.",
        metrics: ["Procédures retrouvées en quelques secondes", "Recherche fournisseur automatisée", "Escalade en cas de faible confiance"]
      },
      {
        sector: "Cabinets d'avocats",
        title: "Préqualification client et préparation du dossier",
        result: "Synthèses le jour même",
        text:
          "Un petit cabinet devait accélérer la qualification des nouveaux dossiers sans perdre la revue professionnelle. Nous avons conçu un processus qui capture les demandes web et téléphone, résume la situation, prépare les questions de suivi et livre un brief prêt pour l'avocat.",
        metrics: ["Nouvelles demandes structurées", "Revue avocat préservée", "Préparation du conflit d'intérêts incluse"]
      },
      {
        sector: "Génération de leads",
        title: "Agent de qualification et transmission commerciale",
        result: "Meilleure qualité des appels réservés",
        text:
          "Une agence de génération de leads qualifiait manuellement les prospects entre formulaires, e-mails, notes CRM et résultats d'appels. Nous avons construit un processus assisté par agent qui enrichit les leads, évalue leur adéquation, rédige les relances et signale les transmissions commerciales fragiles.",
        metrics: ["Score d'adéquation standardisé", "Relances automatisées", "Transmissions CRM plus propres"]
      },
      {
        sector: "E-commerce",
        title: "Support client et tri des retours",
        result: "Support acheteur 24/7",
        text:
          "Une boutique en ligne voulait de meilleures réponses clients sans recruter une équipe complète. Nous avons connecté les données de commandes, les informations produits, les règles d'expédition et l'historique support dans un assistant contrôlé avec règles d'escalade.",
        metrics: ["Questions de commande traitées plus vite", "Retours triés automatiquement", "Règles d'escalade appliquées"]
      },
      {
        sector: "Formation en ligne",
        title: "Copilote d'inscription et support apprenants",
        result: "Réponse lead-inscription accélérée",
        text:
          "Une activité de cours et de coaching devait relancer les leads, répondre aux questions sur les programmes et accompagner les apprenants sur plusieurs canaux. Nous avons créé un processus de qualification, rappels, intégration et réponses depuis la base de connaissances.",
        metrics: ["Suivi des demandes accéléré", "FAQ apprenants automatisée", "Notes d'adéquation programme capturées"]
      }
    ],
    strategySteps: [
      {
        step: "01",
        title: "Diagnostiquer",
        text:
          "Nous interrogeons les parties prenantes, analysons les processus et identifions où l'IA peut supprimer des goulots d'étranglement sans créer de risque opérationnel.",
        output: "Carte d'opportunités IA et synthèse pour la direction"
      },
      {
        step: "02",
        title: "Prioriser",
        text:
          "Nous évaluons les cas d'usage selon la valeur, la complexité, la disponibilité des données, l'urgence et la friction d'adoption.",
        output: "Feuille de route priorisée avec fourchettes budgétaires"
      },
      {
        step: "03",
        title: "Construire",
        text:
          "Nous livrons le premier agent, la première automatisation ou le premier copilote autour de vos outils, rôles et règles de validation réels.",
        output: "Pilote prêt pour la production avec gouvernance"
      },
      {
        step: "04",
        title: "Déployer",
        text:
          "Nous mesurons l'usage, ajustons les processus, formons les équipes et étendons le modèle opérationnel aux prochains départements.",
        output: "Plan de déploiement mesuré et cadence opérationnelle"
      }
    ],
    serviceTiers: [
      {
        name: "Sprint stratégique IA",
        price: "À partir de 2 500 $",
        monthly: "10 jours ouvrables",
        description:
          "Pour les dirigeants qui ont besoin d'un chemin clair avant d'investir dans des outils ou du recrutement.",
        features: [
          "Revue des processus et logiciels",
          "Portefeuille d'opportunités IA",
          "Évaluation ROI et risques",
          "Présentation de la feuille de route à la direction",
          "Recommandation du premier pilote"
        ]
      },
      {
        name: "Pilote opérationnel",
        price: "À partir de 7 500 $",
        monthly: "30-60 jours",
        description:
          "Pour les équipes prêtes à transformer un cas d'usage à forte valeur en processus IA opérationnel, avec une adoption réelle.",
        featured: true,
        features: [
          "Un processus IA prêt pour la production",
          "Intégration outils et données",
          "Règles de validation humaine et d'escalade",
          "Session d'intégration équipe",
          "Tableau de bord de succès"
        ]
      },
      {
        name: "Partenaire opérations IA",
        price: "À partir de 3 500 $/mois",
        monthly: "Feuille de route trimestrielle",
        description:
          "Pour les PME qui veulent une livraison IA continue, de la gouvernance et de l'optimisation sans recruter une équipe IA complète.",
        features: [
          "Cycles mensuels de conception et d'optimisation",
          "Support gouvernance et politique",
          "Sélection des fournisseurs et modèles",
          "Revues formation et adoption",
          "Reporting pour la direction"
        ]
      }
    ],
    faqItems: [
      [
        "Travaillez-vous seulement avec de grandes entreprises ?",
        "Non. Le niveau d'exigence est celui du conseil aux grandes entreprises, mais le modèle de livraison est conçu pour les PME qui veulent des résultats IA concrets sans la lourdeur d'un grand cabinet."
      ],
      [
        "Faites-vous uniquement du conseil ou construisez-vous aussi les solutions ?",
        "Les deux. Nous définissons la stratégie, puis construisons le premier processus ou agent pour transformer la feuille de route en changement opérationnel mesurable."
      ],
      [
        "Pouvez-vous utiliser nos logiciels existants ?",
        "Oui. Nous commençons généralement avec les systèmes déjà en place : CRM, e-mail, tableurs, documents, calendriers, outils de support et bases internes."
      ],
      [
        "Comment réduisez-vous le risque IA ?",
        "Nous concevons les processus avec limites de données, validation humaine, journaux d'audit, règles d'escalade et formation des équipes."
      ]
    ],
    home: {
      eyebrow: "Le chemin clair vers l'IA de niveau entreprise",
      title: "Stratégie IA et agents que votre entreprise peut vraiment utiliser.",
      lede:
        "Schwartz AI Partners aide les PME à passer de l'incertitude autour de l'IA à des systèmes concrets qui améliorent les opérations, les ventes, le support et la décision. Nous définissons la feuille de route, construisons les premiers processus et aidons votre équipe à les adopter avec des contrôles clairs.",
      primaryCta: "Planifier votre feuille de route IA",
      secondaryCta: "Voir les cas clients",
      leadKicker: "Prospection et conversion",
      leadTitle: "Commencez par les cas d'usage qui ont le plus de chances d'obtenir budget et adoption en interne.",
      leadText:
        "Partagez la taille de votre entreprise, vos outils et votre principal goulot d'étranglement. Nous identifierons le premier projet IA le plus clair et le bon chemin vers la mise en œuvre.",
      leadButton: "Demander une revue stratégique IA"
    },
    servicesPage: {
      kicker: "Services",
      title: "Un conseil en IA qui transforme la stratégie en chemin opérationnel clair.",
      text:
        "Nous combinons stratégie de direction, mise en œuvre et accompagnement à l'adoption pour que votre programme IA ne reste ni un slide deck, ni un test de chatbot, ni un simple exercice de sélection fournisseur.",
      deliveryKicker: "Modèle de livraison",
      deliveryTitle: "Discipline de conseil senior, exécution pratique pour PME.",
      deliveryText:
        "Notre travail est conçu pour les entreprises qui ont besoin d'une réflexion structurée, d'une mise en œuvre responsable et d'une séquence claire de gains sans créer une grande fonction IA interne.",
      useCases: [
        "Automatisation des opérations",
        "Copilotes ventes et CRM",
        "Agents de support client",
        "Processus documentaires et reporting",
        "Préqualification cabinet d'avocats et synthèses de dossier",
        "Qualification et enrichissement de leads",
        "Support e-commerce et tri des retours",
        "Parcours d'intégration pour formations en ligne",
        "Assistants de connaissances",
        "Gouvernance et formation IA"
      ]
    },
    caseStudiesPage: {
      kicker: "Cas clients",
      title: "Transformations IA représentatives pour entreprises en croissance.",
      text:
        "Ces exemples montrent le type de travail que nous priorisons : des processus pratiques et mesurables qui réduisent l'effort manuel et améliorent l'expérience client ou employé.",
      proofKicker: "Signaux de confiance",
      proofTitle: "Ce que chaque mission inclut.",
      proofText:
        "Nous ancrons les projets dans la réalité opérationnelle : vrais logiciels, vraies équipes, contrôles clairs et résultats mesurables.",
      proofCards: [
        ["Alignement sponsor exécutif", "Un responsable clair, un processus de décision et un objectif commercial."],
        ["Revue des limites de données", "Ce que l'IA peut consulter, ce qu'elle ne peut pas consulter, et où l'humain garde le contrôle."],
        ["Plan d'adoption", "Formation, documentation du processus et boucles de feedback équipe."],
        ["Tableau de bord ROI", "Usage, temps économisé, conversion, temps de cycle ou indicateurs qualité."]
      ]
    },
    strategyPage: {
      kicker: "Stratégie",
      title: "Un chemin IA clair pour la prospection, la conversion, les opérations et la livraison.",
      text:
        "Pour les PME, la stratégie IA doit se connecter directement au chiffre d'affaires, à la marge et à l'exécution quotidienne. Nous construisons la feuille de route autour des résultats métier, pas autour de la dernière tendance modèle.",
      winsKicker: "Pour dirigeants de PME",
      winsTitle: "Où nous trouvons généralement les premiers gains.",
      winsText:
        "Le meilleur premier projet IA est rarement le plus spectaculaire. C'est le processus avec assez de volume, des données accessibles, une responsabilité claire et un résultat métier mesurable.",
      wins: [
        "Recherche de leads, rédaction de prospection et relances",
        "Enrichissement de prospects et contrôle qualité des appels réservés",
        "Copilotes propositions, devis et briefs clients",
        "Préqualification en cabinet d'avocats, préparation conflits d'intérêts et synthèses dossier",
        "Tri du support et agents de base de connaissances",
        "Questions-réponses produit e-commerce, support commandes et tri des retours",
        "Inscription à des formations en ligne, intégration et support apprenants",
        "Extraction de factures, documents et tâches administratives",
        "Reporting opérationnel et alertes d'exception",
        "Assistants de connaissances internes pour procédures et intégration"
      ]
    },
    tiersPage: {
      kicker: "Offres",
      title: "Choisissez le niveau de support IA que votre entreprise peut vraiment absorber.",
      text:
        "Commencez par la stratégie, passez à un pilote ou retenez-nous comme partenaire opérations. Chaque offre est conçue pour créer des progrès visibles sans submerger l'équipe.",
      fitKicker: "Vérification de l'adéquation",
      fitTitle: "Vous ne savez pas quelle offre choisir ?",
      fitText:
        "Partagez votre contexte métier et nous recommanderons le chemin le plus clair de l'idée au processus IA utile.",
      fitButton: "Demander conseil sur les offres",
      mostPopular: "Le plus choisi",
      serviceTier: "Offre",
      discuss: "Discuter"
    },
    contactPage: {
      kicker: "Contact",
      title: "Expliquez-nous l'opportunité IA que vous voulez débloquer.",
      text:
        "Parlez-nous de votre entreprise, de vos outils actuels et du processus à améliorer. Nous répondrons avec le premier cas d'usage probable, l'offre adaptée et la prochaine étape.",
      intakeKicker: "Brief stratégique",
      intakeTitle: "Demander une session feuille de route IA.",
      intakeText:
        "Ce formulaire envoie votre demande dans notre processus CRM. Incluez votre secteur, la taille de votre équipe, vos outils logiciels et le processus métier que vous voulez améliorer avec l'IA en premier.",
      compareTiers: "Comparer les offres",
      button: "Envoyer le brief stratégique"
    },
    heroVisual: {
      aria: "Modèle opérationnel de transformation IA",
      cockpit: "Cockpit IA Schwartz",
      status: "Feuille de route active",
      tabsAria: "Choisir un exemple de conseil IA",
      businessWorkflow: "Processus métier",
      aiAgent: "Agent IA",
      humanApproval: "Validation humaine",
      signal: "Signal",
      result: "Stratégie, conception, contrôles et adoption restent sur un même chemin clair."
    },
    sections: {
      consultingServices: "Services de conseil",
      ambitionTitle: "De l'ambition IA à la capacité opérationnelle.",
      ambitionText:
        "Nous agissons comme un partenaire de transformation concret : clarifier l'opportunité, construire le système, le connecter à vos outils et aider l'équipe à l'adopter.",
      caseStudiesKicker: "Cas clients",
      caseStudiesTitle: "Programmes IA pratiques pour entreprises qui veulent du ROI, pas du théâtre.",
      caseStudiesText:
        "Nous ciblons les processus où l'IA peut réduire les cycles, diminuer le travail répétitif, améliorer la qualité de réponse ou renforcer le suivi.",
      exploreCaseStudies: "Explorer les cas clients",
      methodKicker: "Méthode de transformation",
      methodTitle: "Une route disciplinée de la stratégie à l'IA livrée.",
      questionsKicker: "Questions",
      questionsTitle: "Ce que les équipes dirigeantes demandent d'abord."
    },
    form: {
      name: "Nom",
      email: "E-mail professionnel",
      company: "Entreprise",
      phone: "Téléphone",
      industry: "Secteur",
      teamSize: "Taille de l'équipe",
      serviceTier: "Offre",
      recommendOne: "Recommandez une offre",
      timeline: "Calendrier",
      preferredContact: "Contact préféré",
      improve: "Que doit améliorer l'IA en premier ?",
      improvePlaceholder:
        "Exemple : relance de leads, création de propositions, tri du support, reporting, processus documentaires, recherche de connaissances internes...",
      priorityNeed: "Stratégie et mise en œuvre IA",
      select: "Choisir",
      any: "Indifférent",
      sending: "Envoi",
      success: "Merci. Votre brief stratégique a été envoyé dans notre processus CRM.",
      error: "La demande CRM a échoué. Écrivez à contact@schwartzaipartners.com et nous vous répondrons.",
      note: "Nous utilisons vos informations uniquement pour répondre à votre opportunité de conseil IA.",
      industries: [
        "Services professionnels",
        "Cabinet d'avocats ou services juridiques",
        "Agence de génération de leads",
        "Agence marketing ou growth",
        "Formation en ligne ou coaching",
        "Santé ou cliniques",
        "Production industrielle",
        "Commerce de détail ou e-commerce",
        "SaaS ou plateforme en ligne",
        "Immobilier ou construction",
        "Hôtellerie-restauration",
        "Autre PME"
      ],
      timelines: ["Ce mois-ci", "Ce trimestre", "Trimestre prochain", "Exploration"],
      preferredContacts: ["E-mail", "Téléphone", "Appel vidéo"]
    }
  },
  de: {
    meta: {
      home: {
        title: "Schwartz AI Partners - KI-Beratung auf Enterprise-Niveau fuer KMU",
        description:
          "Schwartz AI Partners hilft KMU, KI-Strategie zu definieren, KI-Agenten und Automatisierung zu bauen, Adoption zu steuern und ROI zu messen."
      },
      services: {
        title: "Schwartz AI Partners Services",
        description:
          "KI-Opportunity-Audits, KI-Agenten, Automatisierung, Datenintegration, Governance und Adoption fuer kleine und mittlere Unternehmen."
      },
      "case-studies": {
        title: "Schwartz AI Partners Fallstudien",
        description:
          "Repraesentative KI-Beratungsbeispiele fuer Kanzleien, Lead-Generation-Firmen, Ecommerce, Online-Bildung, Sales Operations, lokale Gesundheit, Fertigung und KMU-Operations."
      },
      strategy: {
        title: "Schwartz AI Partners Strategie",
        description:
          "Ein praktischer KI-Strategieprozess fuer Outreach, Closing, Kanzlei-Intake, Lead Generation, Online-Operations, Workflow-Automation, Governance und Rollout."
      },
      tiers: {
        title: "Schwartz AI Partners Leistungspakete",
        description:
          "KI-Beratungsangebote fuer Strategie-Sprints, Pilot-Builds und laufende KI-Operating-Partner-Unterstuetzung."
      },
      contact: {
        title: "Kontakt Schwartz AI Partners",
        description: "Fordern Sie eine KI-Roadmap-Session fuer Ihr kleines oder mittleres Unternehmen an."
      }
    },
    nav: [
      { page: "services", label: "Services" },
      { page: "case-studies", label: "Fallstudien" },
      { page: "strategy", label: "Strategie" },
      { page: "tiers", label: "Pakete" },
      { page: "contact", label: "Kontakt" }
    ],
    ui: {
      navLabel: "Hauptnavigation",
      mobileNavLabel: "Mobile Navigation",
      languageLabel: "Sprache",
      brandAria: "Schwartz AI Partners Startseite",
      brandSubline: "Enterprise-KI fuer KMU",
      cta: "Strategiegespraech buchen",
      footer: "Strategie, Implementierung und Adoption fuer kleine und mittlere Unternehmen.",
      openMenu: "Menue oeffnen",
      closeMenu: "Menue schliessen",
      outcomesLabel: "Erwartete Beratungsergebnisse",
      impactLabel: "Beratungswirkung",
      trustSignalsLabel: "Vertrauenssignale fuer KI-Beratung"
    },
    outcomes: [
      "Klare KI-Roadmap in 10 Arbeitstagen",
      "Erste Automatisierung oder erster Agent in 30 bis 90 Tagen produktiv",
      "Governance, Adoption und ROI-Tracking von Tag eins an integriert"
    ],
    trustSignals: [
      { value: "KMU", label: "Gebaut fuer kleine und mittlere Unternehmen" },
      { value: "30-90", label: "Tage bis zum ersten produktiven Workflow" },
      { value: "Ops + KI", label: "Strategie, Build, Integration und Adoption" },
      { value: "Human-led", label: "Kontrollen, Review-Pfade und Governance" }
    ],
    services: [
      {
        title: "KI-Opportunity-Audit",
        text:
          "Workflows, Daten, Software und Team-Reibung abbilden, um den klarsten Weg zu nuetzlicher KI zu definieren.",
        points: ["Use-Case-Portfolio", "ROI- und Aufwandsscoring", "Risiko- und Datenbereitschaftsreview"]
      },
      {
        title: "KI-Agenten und Automatisierung",
        text:
          "KI-Agenten entwerfen, implementieren und verbinden, die wiederholbare Wissensarbeit in Operations, Sales, Support und Administration uebernehmen.",
        points: ["CRM- und Email-Agenten", "Dokumenten-Workflows", "Menschliche Freigabepfade"]
      },
      {
        title: "Daten- und Tool-Integration",
        text:
          "KI mit den Systemen verbinden, die Ihr Unternehmen bereits nutzt, ohne eine Plattformmigration zu erzwingen.",
        points: ["CRM, Tabellen, Postfaecher", "Wissensdatenbanken", "APIs und interne Tools"]
      },
      {
        title: "Governance und Adoption",
        text:
          "Policies, Berechtigungen, Validierungsregeln und Training aufsetzen, damit Teams die Systeme sicher und tatsaechlich nutzen.",
        points: ["KI-Nutzungsrichtlinie", "Team Enablement", "Monitoring und Verbesserung"]
      }
    ],
    caseStudies: [
      {
        sector: "B2B Services",
        title: "Sales-Operations-Copilot",
        result: "12 Stunden pro Woche gespart",
        text:
          "Ein wachsendes Dienstleistungsunternehmen brauchte saubereres Lead-Follow-up und bessere Angebotsvorbereitung. Wir verbanden CRM-Notizen, Email-Historie und Templates zu einem gefuehrten Workflow, der Follow-ups entwirft und Account-Briefings zur Review vorbereitet.",
        metrics: ["Lead-Reaktionszeit 68% niedriger", "Pipeline-Notizen standardisiert", "Keine CRM-Migration"]
      },
      {
        sector: "Lokale Gesundheit",
        title: "Patienten-Intake und Admin-Automation",
        result: "2,4x schnellerer Intake",
        text:
          "Ein Klinikteam verbrachte zu viel Zeit damit, Formulare, Anrufe und Emails in strukturierte Aufgaben zu verwandeln. Wir bauten einen kontrollierten Intake-Workflow, der Anfragen zusammenfasst, Dringlichkeit taggt und Arbeit an die richtige Person routet.",
        metrics: ["40+ Anfragen taeglich bearbeitet", "Menschliche Review erhalten", "Klare Audit-Spur"]
      },
      {
        sector: "Leichte Fertigung",
        title: "Operations-Wissensassistent",
        result: "Weniger wiederholte Unterbrechungen",
        text:
          "Ein Fertigungs-KMU hatte Verfahren ueber Dateien und informelles Wissen verstreut. Wir erstellten einen durchsuchbaren Assistenten ueber SOPs, Lieferantendokumente und interne Notizen, mit Eskalation bei niedriger Sicherheit.",
        metrics: ["SOPs in Sekunden gefunden", "Lieferantensuche automatisiert", "Low-Confidence-Handoff"]
      },
      {
        sector: "Kanzleien",
        title: "Mandanten-Intake und Aktenbriefing",
        result: "Intake-Zusammenfassungen am selben Tag",
        text:
          "Eine kleine Kanzlei musste den Intake beschleunigen, ohne professionelle Review zu verlieren. Wir entwickelten einen Workflow, der Web- und Telefonanfragen erfasst, die Angelegenheit zusammenfasst, Folgefragen entwirft und ein anwaltsreifes Briefing vorbereitet.",
        metrics: ["Neue Anfragen strukturiert", "Anwaltsreview erhalten", "Konfliktcheck-Vorbereitung enthalten"]
      },
      {
        sector: "Lead Generation",
        title: "Lead-Qualifizierung und Handoff-Agent",
        result: "Hoehere Qualitaet gebuchter Calls",
        text:
          "Eine Lead-Generation-Firma qualifizierte Interessenten manuell ueber Formulare, Emails, CRM-Notizen und Call-Ergebnisse. Wir bauten einen agentengestuetzten Workflow, der Leads anreichert, Fit scored, Follow-ups entwirft und schwache Handoffs vor Sales Calls markiert.",
        metrics: ["Fit-Scoring standardisiert", "Follow-up-Entwuerfe automatisiert", "CRM-Handoffs sauberer"]
      },
      {
        sector: "Ecommerce",
        title: "Kundensupport und Retouren-Triage",
        result: "24/7 Kaeufersupport-Abdeckung",
        text:
          "Ein Online-Shop brauchte bessere Kundenantworten, ohne ein komplettes Supportteam aufzubauen. Wir verbanden Bestelldaten, Produktinformationen, Versandregeln und Helpdesk-Historie zu einem kontrollierten Supportassistenten mit Eskalationsregeln.",
        metrics: ["Bestellfragen schneller beantwortet", "Retouren automatisch triagiert", "Eskalationsregeln durchgesetzt"]
      },
      {
        sector: "Online-Bildung",
        title: "Enrollment- und Studentensupport-Copilot",
        result: "Schnellere Lead-zu-Enrollment-Reaktion",
        text:
          "Ein Kurs- und Coaching-Business musste Leads nachfassen, Programmfragen beantworten und eingeschriebene Studierende ueber mehrere Kanaele unterstuetzen. Wir bauten einen Workflow fuer Qualifizierung, Reminder, Onboarding und Wissensdatenbank-Antworten.",
        metrics: ["Anfrage-Follow-up beschleunigt", "Studenten-FAQs automatisiert", "Programm-Fit-Notizen erfasst"]
      }
    ],
    strategySteps: [
      {
        step: "01",
        title: "Diagnostizieren",
        text:
          "Wir interviewen Stakeholder, pruefen Workflows und identifizieren, wo KI Engpaesse entfernt, ohne operatives Risiko zu schaffen.",
        output: "KI-Opportunity-Map und Executive Summary"
      },
      {
        step: "02",
        title: "Priorisieren",
        text:
          "Wir bewerten Use Cases nach Wert, Komplexitaet, Datenbereitschaft, Dringlichkeit und Adoptionsreibung.",
        output: "Priorisierte Roadmap mit Budgetbaendern"
      },
      {
        step: "03",
        title: "Bauen",
        text:
          "Wir liefern den ersten Agenten, die erste Automatisierung oder den ersten Copilot rund um Ihre realen Tools, Rollen und Freigaberegeln.",
        output: "Produktionsreifer Pilot mit Governance"
      },
      {
        step: "04",
        title: "Skalieren",
        text:
          "Wir messen Nutzung, tunen Workflows, schulen Teams und rollen das Operating Model in die naechsten Abteilungen aus.",
        output: "Gemessener Rollout-Plan und operative Kadenz"
      }
    ],
    serviceTiers: [
      {
        name: "KI-Strategie-Sprint",
        price: "Ab $2,500",
        monthly: "10 Arbeitstage",
        description:
          "Fuer Inhaber und Fuehrungsteams, die einen klaren Weg brauchen, bevor sie in Tools oder Personal investieren.",
        features: [
          "Workflow- und Software-Review",
          "KI-Opportunity-Portfolio",
          "ROI- und Risiko-Scoring",
          "Executive-Roadmap-Praesentation",
          "Empfehlung fuer den ersten Piloten"
        ]
      },
      {
        name: "Pilot Build",
        price: "Ab $7,500",
        monthly: "30-60 Tage",
        description:
          "Fuer Teams, die einen hochwertigen Use Case in einen funktionierenden KI-Workflow mit echter Adoption verwandeln wollen.",
        featured: true,
        features: [
          "Ein produktionsreifer KI-Workflow",
          "Tool- und Datenintegration",
          "Menschliche Freigabe- und Eskalationsregeln",
          "Team-Onboarding-Session",
          "Success-Metrics-Dashboard"
        ]
      },
      {
        name: "KI Operating Partner",
        price: "Ab $3,500/Monat",
        monthly: "Quartals-Roadmap",
        description:
          "Fuer KMU, die laufende KI-Lieferung, Governance und Optimierung wollen, ohne ein komplettes internes KI-Team einzustellen.",
        features: [
          "Monatliche Build- und Optimierungszyklen",
          "Governance- und Policy-Support",
          "Vendor- und Modellauswahl",
          "Training- und Adoptionsreviews",
          "Leadership Reporting"
        ]
      }
    ],
    faqItems: [
      [
        "Arbeiten Sie nur fuer Enterprise-Unternehmen?",
        "Nein. Der Markenstandard ist enterprise-grade, aber das Liefermodell ist fuer KMU gebaut, die praktische KI-Ergebnisse ohne Enterprise-Beratungsaufwand brauchen."
      ],
      [
        "Beraten Sie nur oder bauen Sie auch?",
        "Beides. Wir definieren die Strategie und bauen danach den ersten Workflow oder Agenten, damit die Roadmap in messbare operative Veraenderung uebergeht."
      ],
      [
        "Koennen Sie unsere bestehende Software nutzen?",
        "Ja. Wir starten meist mit den Systemen, die bereits vorhanden sind: CRM, Email, Tabellen, Dokumente, Kalender, Supporttools und interne Datenbanken."
      ],
      [
        "Wie reduzieren Sie KI-Risiko?",
        "Wir entwerfen Workflows mit Datengrenzen, menschlicher Freigabe, Audit Logs, Eskalationsregeln und Teamtraining, statt KI als Black Box zu behandeln."
      ]
    ],
    home: {
      eyebrow: "Der klare Weg zu Enterprise-KI",
      title: "KI-Strategie und Agenten, die Ihr Unternehmen wirklich nutzen kann.",
      lede:
        "Schwartz AI Partners hilft KMU, von KI-Unsicherheit zu praktischen Systemen zu kommen, die Operations, Sales, Support und Entscheidungen verbessern. Wir definieren die Roadmap, bauen die ersten Workflows und helfen Ihrem Team, sie mit klaren Kontrollen zu nutzen.",
      primaryCta: "KI-Roadmap planen",
      secondaryCta: "Use Cases ansehen",
      leadKicker: "Outreach und Closing",
      leadTitle: "Starten Sie mit den Use Cases, die Budget und Team-Adoption am wahrscheinlichsten gewinnen.",
      leadText:
        "Senden Sie Unternehmensgroesse, Tools und wichtigsten Engpass. Wir identifizieren das klarste erste KI-Projekt und den passenden Weg zur Implementierung.",
      leadButton: "KI-Strategiereview anfragen"
    },
    servicesPage: {
      kicker: "Services",
      title: "KI-Beratung, die Strategie in einen klaren operativen Weg verwandelt.",
      text:
        "Wir kombinieren Executive-Strategie, Implementierung und Adoption-Support, damit Ihr KI-Programm kein Slide Deck, kein Chatbot-Experiment und keine Vendor-Shopping-Uebung bleibt.",
      deliveryKicker: "Delivery-Modell",
      deliveryTitle: "Senior-Beratungsdisziplin, praktische KMU-Umsetzung.",
      deliveryText:
        "Unsere Arbeit ist fuer Unternehmen konzipiert, die strukturiertes Denken, verantwortungsvolle Implementierung und eine klare Sequenz von Erfolgen brauchen, ohne eine grosse interne KI-Funktion aufzubauen.",
      useCases: [
        "Operations-Automatisierung",
        "Sales- und CRM-Copilots",
        "Kundensupport-Agenten",
        "Dokumenten- und Reporting-Workflows",
        "Kanzlei-Intake und Aktenzusammenfassungen",
        "Lead-Qualifizierung und Anreicherung",
        "Ecommerce-Support und Retouren-Triage",
        "Online-Kurs-Onboarding-Workflows",
        "Wissensassistenten",
        "KI-Governance und Training"
      ]
    },
    caseStudiesPage: {
      kicker: "Fallstudien",
      title: "Repraesentative KI-Transformationen fuer wachsende Unternehmen.",
      text:
        "Diese Beispiele zeigen die Art von Arbeit, die wir priorisieren: praktische, messbare Workflows, die manuellen Aufwand reduzieren und Kunden- oder Mitarbeitererfahrung verbessern.",
      proofKicker: "Trust Signals",
      proofTitle: "Was jede Zusammenarbeit umfasst.",
      proofText:
        "Wir halten Projekte in der operativen Realitaet: echte Software, echte Teams, klare Kontrollen und messbare Ergebnisse.",
      proofCards: [
        ["Executive-Sponsor-Alignment", "Ein klarer Owner, Entscheidungsprozess und kommerzielles Ziel."],
        ["Datengrenzen-Review", "Worauf KI zugreifen darf, worauf nicht, und wo Menschen die Kontrolle behalten."],
        ["Adoptionsplan", "Training, Workflow-Dokumentation und Team-Feedback-Loops."],
        ["ROI-Dashboard", "Nutzung, gesparte Zeit, Conversion, Cycle Time oder Qualitaetsmetriken."]
      ]
    },
    strategyPage: {
      kicker: "Strategie",
      title: "Ein klarer KI-Weg fuer Outreach, Closing, Operations und Delivery.",
      text:
        "Fuer KMU muss KI-Strategie direkt mit Umsatz, Marge und taeglicher Umsetzung verbunden sein. Wir bauen die Roadmap um Business-Ergebnisse, nicht um Modell-Hype.",
      winsKicker: "Fuer KMU-Fuehrung",
      winsTitle: "Wo wir meist die ersten Erfolge finden.",
      winsText:
        "Das beste erste KI-Projekt ist selten das spektakulaerste. Es ist der Workflow mit genug Volumen, zugaenglichen Daten, klarer Ownership und messbarem Business-Ergebnis.",
      wins: [
        "Lead-Recherche, Outreach-Entwuerfe und Follow-up-Workflows",
        "Lead-Gen-Prospect-Anreicherung und QA gebuchter Calls",
        "Proposal-, Quote- und Kundenbriefing-Copilots",
        "Kanzlei-Intake, Konfliktcheck-Vorbereitung und Aktenzusammenfassungen",
        "Support-Triage und Wissensdatenbank-Agenten",
        "Ecommerce-Produkt-Q&A, Bestellsupport und Retouren-Triage",
        "Online-Kurs-Enrollment, Onboarding und Studentensupport",
        "Rechnungs-, Dokumenten- und Admin-Extraktionsworkflows",
        "Operations-Reporting und Exception Alerts",
        "Interne Wissensassistenten fuer SOPs und Onboarding"
      ]
    },
    tiersPage: {
      kicker: "Pakete",
      title: "Waehlen Sie das KI-Support-Level, das Ihr Unternehmen wirklich aufnehmen kann.",
      text:
        "Starten Sie mit Strategie, gehen Sie in einen Piloten oder behalten Sie uns als Operating Partner. Jedes Paket ist darauf ausgelegt, sichtbaren Fortschritt zu schaffen, ohne das Team zu ueberfordern.",
      fitKicker: "Fit Check",
      fitTitle: "Nicht sicher, welches Paket passt?",
      fitText:
        "Teilen Sie Ihren Business-Kontext und wir empfehlen den klarsten Weg von der Idee zum nuetzlichen KI-Workflow.",
      fitButton: "Pakete besprechen",
      mostPopular: "Am beliebtesten",
      serviceTier: "Leistungspaket",
      discuss: "Besprechen"
    },
    contactPage: {
      kicker: "Kontakt",
      title: "Briefen Sie uns zu der KI-Chance, die Sie erschliessen wollen.",
      text:
        "Erzaehlen Sie uns von Ihrem Unternehmen, aktuellen Tools und dem Workflow, den Sie verbessern wollen. Wir antworten mit dem wahrscheinlichen ersten Use Case, passenden Paket und naechsten Schritt.",
      intakeKicker: "Strategie-Intake",
      intakeTitle: "KI-Roadmap-Session anfragen.",
      intakeText:
        "Dieses Formular sendet Ihre Anfrage durch unseren CRM-Workflow. Geben Sie Branche, Teamgroesse, Software-Stack und den Business-Prozess an, den KI zuerst verbessern soll.",
      compareTiers: "Pakete vergleichen",
      button: "Strategiebrief senden"
    },
    heroVisual: {
      aria: "Operating Model fuer KI-Transformation",
      cockpit: "Schwartz KI-Cockpit",
      status: "Roadmap live",
      tabsAria: "KI-Beratungsbeispiel auswaehlen",
      businessWorkflow: "Business Workflow",
      aiAgent: "KI-Agent",
      humanApproval: "Menschliche Freigabe",
      signal: "Signal",
      result: "Strategie, Build, Kontrollen und Adoption bleiben auf einem klaren Weg."
    },
    sections: {
      consultingServices: "Beratungsservices",
      ambitionTitle: "Von KI-Ambition zu operativer Faehigkeit.",
      ambitionText:
        "Wir agieren als praktischer Transformationspartner: Opportunity klaeren, System bauen, mit Ihren Tools verbinden und dem Team bei der Adoption helfen.",
      caseStudiesKicker: "Fallstudien",
      caseStudiesTitle: "Praktische KI-Programme fuer Unternehmen, die ROI brauchen, kein Theater.",
      caseStudiesText:
        "Wir fokussieren Workflows, in denen KI Cycle Time verkuerzt, repetitive Arbeit reduziert, Antwortqualitaet verbessert oder Follow-up staerkt.",
      exploreCaseStudies: "Fallstudien ansehen",
      methodKicker: "Transformationsmethode",
      methodTitle: "Ein disziplinierter Weg von Strategie zu gelieferter KI.",
      questionsKicker: "Fragen",
      questionsTitle: "Was Fuehrungsteams zuerst fragen."
    },
    form: {
      name: "Name",
      email: "Geschaeftliche Email",
      company: "Unternehmen",
      phone: "Telefon",
      industry: "Branche",
      teamSize: "Teamgroesse",
      serviceTier: "Leistungspaket",
      recommendOne: "Empfehlung anfragen",
      timeline: "Zeitrahmen",
      preferredContact: "Bevorzugter Kontakt",
      improve: "Was soll KI zuerst verbessern?",
      improvePlaceholder:
        "Beispiel: Lead-Follow-up, Angebotserstellung, Support-Triage, Reporting, Dokumenten-Workflows, interne Wissenssuche...",
      priorityNeed: "KI-Beratungsstrategie und Implementierung",
      select: "Auswaehlen",
      any: "Beliebig",
      sending: "Senden",
      success: "Danke. Ihr Strategiebrief wurde an unseren CRM-Workflow gesendet.",
      error: "Die CRM-Anfrage ist fehlgeschlagen. Schreiben Sie an contact@schwartzaipartners.com und wir melden uns.",
      note: "Wir verwenden Ihre Angaben nur, um zu Ihrer KI-Beratungsmoeglichkeit zu antworten.",
      industries: [
        "Professional Services",
        "Kanzlei oder Rechtsdienstleistungen",
        "Lead-Generation-Agentur",
        "Marketing- oder Growth-Agentur",
        "Online-Bildung oder Coaching",
        "Gesundheit oder Kliniken",
        "Fertigung",
        "Retail oder Ecommerce",
        "SaaS oder Online-Plattform",
        "Immobilien oder Bau",
        "Hospitality",
        "Andere KMU"
      ],
      timelines: ["Diesen Monat", "Dieses Quartal", "Naechstes Quartal", "Exploration"],
      preferredContacts: ["Email", "Telefon", "Video Call"]
    }
  }
};
